import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { TWclassNames } from "../../../utils/Div";
import {
  collectFlowEffects,
  getCurrentQuestion,
  getNextQuestionNodeId,
  getQuestionNodes,
  getStartNodeId,
  getVisitedQuestionNodes,
  normalizeReportGuideTemplate,
  resolveGuideQuestionNavigation,
} from "./reportGuideFlow";

export default function ReportGuideOnboardingDialog({
  open,
  template,
  report,
  onClose,
  onComplete,
  purpose = "field_report",
  isSaving = false,
}) {
  const { t } = useTranslation();
  const normalizedTemplate = useMemo(() => normalizeReportGuideTemplate(template), [template]);
  const flow = normalizedTemplate?.settings?.flow;
  const questions = useMemo(() => getQuestionNodes(flow), [flow]);
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [history, setHistory] = useState([]);
  const [answers, setAnswers] = useState({});

  const currentQuestionNode = getCurrentQuestion(flow, currentNodeId);
  const currentQuestion = currentQuestionNode?.data;
  const currentQuestionKey = currentQuestion?.key || currentQuestionNode?.id;
  const currentValue = answers[currentQuestionKey];
  const canContinue =
    !currentQuestion?.required ||
    (Array.isArray(currentValue)
      ? currentValue.length > 0
      : currentValue !== undefined && currentValue !== "");
  const { nextQuestionNodeId } =
    canContinue && currentQuestionNode
      ? resolveGuideQuestionNavigation(flow, currentQuestionNode, currentValue, answers)
      : { nextQuestionNodeId: null };
  const isLastStep =
    Boolean(currentQuestionNode) && canContinue && nextQuestionNodeId === null;
  const visitedQuestions = useMemo(() => getVisitedQuestionNodes(flow, answers), [flow, answers]);
  const currentIndex = currentNodeId ? Math.max(0, visitedQuestions.findIndex((node) => node.id === currentNodeId)) : 0;
  const progress = questions.length ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const effects = useMemo(
    () => collectFlowEffects(normalizedTemplate, answers),
    [normalizedTemplate, answers]
  );
  const reportTextCount = (effects.report_text || []).length;
  const internalTextCount = (effects.internal_report_text || []).length;

  useEffect(() => {
    if (!open) return;
    setCurrentNodeId(getNextQuestionNodeId(flow, getStartNodeId(flow), {}));
    setHistory([]);
    setAnswers({});
  }, [open, normalizedTemplate?.id, report?.id, flow]);

  const setAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const toggleMultiValue = (key, value) => {
    const current = Array.isArray(answers[key]) ? answers[key] : [];
    setAnswer(
      key,
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    );
  };

  const complete = () => {
    onComplete?.({
      report_guide_template_id: normalizedTemplate.id,
      purpose,
      answers,
      effects,
    });
  };

  const close = () => {
    if (isSaving) return;
    setCurrentNodeId(getNextQuestionNodeId(flow, getStartNodeId(flow), {}));
    setHistory([]);
    setAnswers({});
    onClose?.();
  };

  const goBack = () => {
    if (history.length === 0) {
      close();
      return;
    }

    const previousNodeId = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setCurrentNodeId(previousNodeId);
  };

  const goNext = () => {
    if (isLastStep) {
      complete();
      return;
    }

    setHistory((prev) => [...prev, currentNodeId]);
    setCurrentNodeId(nextQuestionNodeId);
  };

  return (
    <PMDialog
      open={open}
      onClose={close}
      size="4xl"
      title={normalizedTemplate?.title || t("Rapportguide")}
      zIndex={3000}
      panelClassName="p-0 overflow-hidden"
    >
      <div className="flex min-h-[560px] flex-col bg-slate-950 text-white">
        <div className="relative overflow-hidden px-6 py-6">
          <div className="absolute inset-0 bg-gradient-to-br from-sky-500/25 via-emerald-500/10 to-amber-500/20" />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-sky-100 ring-1 ring-white/20">
                <SparklesIcon className="h-4 w-4" />
                {t("Guidet rapport")}
              </div>
              <h2 className="mt-4 text-2xl font-semibold">
                {normalizedTemplate?.title || t("Rapportguide")}
              </h2>
              {normalizedTemplate?.description ? (
                <p className="mt-2 max-w-2xl text-sm text-slate-200">
                  {normalizedTemplate.description}
                </p>
              ) : null}
              {report?.displayTitle ? (
                <p className="mt-3 text-xs text-slate-300">{report.displayTitle}</p>
              ) : null}
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-3 text-right ring-1 ring-white/20">
              <p className="text-xs text-slate-300">{t("Steg")}</p>
              <p className="text-lg font-semibold">
                {questions.length ? `${currentIndex + 1}/${questions.length}` : "0/0"}
              </p>
            </div>
          </div>
          <div className="relative mt-6 h-2 overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-sky-400 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="grid flex-1 grid-cols-1 gap-0 bg-white text-gray-900 lg:grid-cols-[1fr_300px]">
          <div className="p-6">
            {!currentQuestion ? (
              <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-500">
                {t("Ingen guide-spørsmål er konfigurert.")}
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium uppercase tracking-wide text-sky-700">
                  {t("Spørsmål")}
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-gray-950">{currentQuestion.label}</h3>
                {currentQuestion.help_text ? (
                  <p className="mt-2 text-sm text-gray-500">{currentQuestion.help_text}</p>
                ) : null}

                <div className="mt-6">
                  {["single_select", "multi_select"].includes(currentQuestion.answer_type) ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {(currentQuestion.options || []).map((option) => {
                        const selected =
                          currentQuestion.answer_type === "multi_select"
                            ? Array.isArray(currentValue) && currentValue.includes(option.value)
                            : currentValue === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                          onClick={() =>
                            currentQuestion.answer_type === "multi_select"
                              ? toggleMultiValue(currentQuestionKey, option.value)
                              : setAnswer(currentQuestionKey, option.value)
                          }
                            className={TWclassNames(
                              "rounded-2xl border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md",
                              selected ? "border-sky-500 bg-sky-50 shadow-sm" : "border-gray-200 bg-white"
                            )}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <span className="font-medium text-gray-950">{option.label}</span>
                              {selected ? <CheckCircleIcon className="h-5 w-5 text-sky-600" /> : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}

                  {currentQuestion.answer_type === "boolean" ? (
                    <div className="grid grid-cols-2 gap-3">
                      {[true, false].map((value) => (
                        <button
                          key={String(value)}
                          type="button"
                          onClick={() => setAnswer(currentQuestionKey, value)}
                          className={TWclassNames(
                            "rounded-2xl border p-5 text-center font-medium",
                            currentValue === value
                              ? "border-sky-500 bg-sky-50 text-sky-800"
                              : "border-gray-200"
                          )}
                        >
                          {value ? t("Ja") : t("Nei")}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  {["text", "date"].includes(currentQuestion.answer_type) ? (
                    <input
                      type={currentQuestion.answer_type === "date" ? "date" : "text"}
                      value={currentValue || ""}
                      onChange={(e) => setAnswer(currentQuestionKey, e.target.value)}
                      className="block w-full rounded-xl border-gray-300 text-sm shadow-sm focus:border-sky-500 focus:ring-sky-500"
                    />
                  ) : null}

                  {currentQuestion.answer_type === "textarea" ? (
                    <textarea
                      rows={5}
                      value={currentValue || ""}
                      onChange={(e) => setAnswer(currentQuestionKey, e.target.value)}
                      className="block w-full rounded-xl border-gray-300 text-sm shadow-sm focus:border-sky-500 focus:ring-sky-500"
                    />
                  ) : null}
                </div>
              </div>
            )}
          </div>

          <aside className="border-l border-gray-200 bg-gray-50 p-5">
            <h4 className="text-sm font-semibold text-gray-900">{t("Oppsummering")}</h4>
            <div className="mt-4 space-y-3">
              {visitedQuestions.map((questionNode) => {
                const question = questionNode.data || {};
                const key = question.key || questionNode.id;
                return (
                <div key={questionNode.id} className="rounded-lg bg-white p-3 shadow-sm">
                  <p className="text-xs font-medium text-gray-500">{question.label}</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatAnswer(question, answers[key]) || "-"}
                  </p>
                </div>
                );
              })}
            </div>
            {reportTextCount > 0 || internalTextCount > 0 ? (
              <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800">
                <div className="flex gap-2">
                  <ClipboardDocumentCheckIcon className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    {t("Guiden kan fylle rapporttekst og lagrer svar for administrasjon.")}
                  </span>
                </div>
              </div>
            ) : null}
          </aside>
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 bg-white p-4 text-gray-900">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {history.length === 0 ? t("Avbryt") : t("Tilbake")}
          </button>
          <button
            type="button"
            disabled={!canContinue || questions.length === 0 || isSaving}
            onClick={goNext}
            className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {isLastStep ? (isSaving ? t("Lagrer...") : t("Bruk guide")) : t("Neste")}
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </PMDialog>
  );
}

function formatAnswer(question, value) {
  if (value === undefined || value === null || value === "") return "";
  const options = question.options || [];
  if (Array.isArray(value)) {
    return value
      .map((item) => options.find((option) => option.value === item)?.label || item)
      .join(", ");
  }
  if (typeof value === "boolean") return value ? "Ja" : "Nei";
  return options.find((option) => option.value === value)?.label || value;
}
